import {Controller} from '@hotwired/stimulus'

let pickerLibrary

export default class extends Controller {
  static targets = ['dialog', 'form', 'credential', 'spreadsheet', 'sheet', 'filename', 'name', 'preview', 'headers', 'error', 'create', 'browse', 'loading', 'setup', 'results', 'partitionReview']
  static values = {metadataUrl: String, previewUrl: String, autoOpen: Boolean}

  connect() {
    this.revision = 0
    this.submitting = false
    // Straight back from connecting Google: go on to picking a sheet. Cleared so a
    // Turbo cache restore of this page doesn't reopen it.
    if (this.autoOpenValue) {
      this.autoOpenValue = false
      this.open()
    }
  }

  disconnect() {
    this.revision += 1
    this.readAbort?.abort()
    this.picker?.dispose()
    this.dialogTarget.close()
  }

  // Inside a closed <details> (the data-tables menu) the dialog would be modal yet
  // invisible, locking the page — so reopen the menu first.
  open() {
    this.element.closest('details')?.setAttribute('open', '')
    this.dialogTarget.showModal()
  }
  close() {
    this.invalidate()
    this.dialogTarget.close()
  }

  invalidate({keepColumnTypes = false} = {}) {
    if (keepColumnTypes) this.rememberColumnTypes()
    else this.columnTypeChoices = null
    this.backToSetup()
    this.revision += 1
    this.readAbort?.abort()
    this.readAbort = new AbortController()
    this.loadingTarget.textContent = ''
    this.previewTarget.removeAttribute('aria-busy')
    this.createTarget.disabled = true
    this.previewTarget.replaceChildren()
    this.headersTarget.replaceChildren()
    this.errorTarget.textContent = ''
  }

  accountChanged() {
    if (this.submitting) return
    this.invalidate()
    this.spreadsheetTarget.value = ''
    this.filenameTarget.textContent = 'No spreadsheet selected'
    this.sheetTarget.replaceChildren(new Option('Browse a spreadsheet first', ''))
    this.sheetTarget.disabled = true
  }

  async sheetChanged() {
    if (this.submitting) return
    this.invalidate()
    if (!this.nameTarget.value || this.nameTarget.value === this.defaultName) {
      this.defaultName = `${this.spreadsheetTitle} — ${this.sheetTarget.selectedOptions[0]?.textContent || 'Sheet'}`
      this.nameTarget.value = this.defaultName
    }
    await this.preview()
  }

  async browse() {
    if (this.submitting) return
    this.invalidate()
    const revision = this.revision
    this.browseTarget.disabled = true
    try {
      const option = this.credentialTarget.selectedOptions[0]
      const session = await this.request(option.dataset.sessionUrl)
      await this.loadPicker()
      if (this.submitting || revision !== this.revision) return
      this.dialogTarget.close() // Google's iframe must be outside the dialog top layer.
      this.picker = new google.picker.PickerBuilder()
        .addView(new google.picker.DocsView(google.picker.ViewId.SPREADSHEETS).setIncludeFolders(false))
        .setOAuthToken(session.access_token)
        .setDeveloperKey(session.api_key)
        .setAppId(String(session.project_number))
        .setOrigin(window.location.origin)
        .setCallback(data => this.picked(data, revision))
        .build()
      this.picker.setVisible(true)
    } catch (error) {
      if (revision !== this.revision) return
      this.errorTarget.textContent = error.message
      if (!this.dialogTarget.open) this.open()
    } finally {
      this.browseTarget.disabled = false
    }
  }

  async picked(data, revision) {
    if (this.submitting || revision !== this.revision) return
    if (![google.picker.Action.PICKED, google.picker.Action.CANCEL].includes(data.action)) return
    this.open()
    if (data.action === google.picker.Action.CANCEL) return
    const file = data.docs[0]
    this.spreadsheetTarget.value = file.id
    this.filenameTarget.textContent = file.name
    this.sheetTarget.replaceChildren(new Option('Loading worksheets…', ''))
    this.sheetTarget.disabled = true
    this.loadingTarget.textContent = 'Loading worksheets…'
    try {
      const result = await this.request(this.metadataUrlValue, new FormData(this.formTarget))
      if (this.submitting || revision !== this.revision) return
      this.sheetTarget.replaceChildren(...result.sheets.map(sheet => new Option(sheet.title, sheet.sheetId)))
      this.sheetTarget.disabled = !result.sheets.length
      this.spreadsheetTitle = result.title
      this.defaultName = `${result.title} — ${result.sheets[0]?.title || 'Sheet'}`
      this.nameTarget.value = this.defaultName
      if (!result.sheets.length) throw new Error('This spreadsheet has no worksheets to preview.')
      await this.preview()
    } catch (error) {
      if (revision === this.revision) {
        this.loadingTarget.textContent = ''
        this.errorTarget.textContent = error.message
      }
    }
  }

  async preview() {
    if (this.submitting) return
    this.invalidate({keepColumnTypes: true})
    if (!this.spreadsheetTarget.value || !this.sheetTarget.value || !this.formTarget.reportValidity()) return
    const revision = this.revision
    this.loadingTarget.textContent = 'Loading the first six rows…'
    this.previewTarget.setAttribute('aria-busy', 'true')
    try {
      const result = await this.request(this.previewUrlValue, new FormData(this.formTarget))
      if (this.submitting || revision !== this.revision) return
      const table = document.createElement('table')
      table.className = 'min-w-full text-left text-xs'
      const thead = table.createTHead()
      thead.className = 'sticky top-0 z-10 bg-white'
      const heading = thead.insertRow()
      const previousTypes = this.columnTypeChoices && JSON.stringify(this.columnTypeChoices.headers) === JSON.stringify(result.headers)
        ? this.columnTypeChoices.types : []
      this.previewHeaders = result.headers
      result.columns.forEach((column, index) => {
        const th = document.createElement('th')
        th.className = 'border-b border-gray-200 px-2 py-1.5 font-semibold'
        const label = document.createElement('span')
        label.textContent = column.name
        const select = document.createElement('select')
        select.name = 'data_source[other_config][google_sheets][column_types][]'
        select.dataset.columnType = ''
        select.setAttribute('aria-label', `Data type for ${column.name}`)
        select.className = 'mt-1 block w-full min-w-24 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-normal'
        result.allowed_types.forEach(type => select.add(new Option(type, type)))
        select.value = result.allowed_types.includes(previousTypes[index]) ? previousTypes[index] : column.type
        th.append(label, select)
        heading.append(th)
      })
      const body = table.createTBody()
      result.parsed_preview.forEach(row => {
        const tr = body.insertRow()
        result.headers.forEach(header => {
          const td = tr.insertCell()
          td.className = 'border-b border-gray-100 px-2 py-1'
          td.textContent = String(row[header] ?? '')
        })
      })
      this.previewTarget.replaceChildren(table)
      this.headersTarget.replaceChildren(...result.headers.map(header => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'data_source[other_config][google_sheets][headers][]'
        input.value = header
        return input
      }))
      this.createTarget.disabled = false
      this.loadingTarget.textContent = result.parsed_preview.length ? 'Preview ready.' : 'No populated cells in the first six rows. Choose the column types before importing.'
    } catch (error) {
      if (revision === this.revision) {
        this.loadingTarget.textContent = ''
        this.errorTarget.textContent = error.message
      }
    } finally {
      if (revision === this.revision) this.previewTarget.removeAttribute('aria-busy')
    }
  }

  rememberColumnTypes() {
    const selects = this.previewTarget.querySelectorAll('select[data-column-type]')
    if (selects.length) {
      this.columnTypeChoices = {headers: this.previewHeaders, types: Array.from(selects, select => select.value)}
    }
  }

  partitionReviewTargetConnected(element) {
    this.submitting = false
    this.formTarget.removeAttribute('aria-busy')
    this.errorTarget.textContent = ''
    this.createTarget.disabled = false
    this.setupTarget.hidden = true
    element.querySelector('h3')?.focus()
  }

  backToSetup() {
    if (this.submitting) return
    this.setupTarget.hidden = false
    this.resultsTarget.replaceChildren()
  }

  submit(event) {
    if (this.submitting || this.createTarget.disabled) { event.preventDefault(); return }
    this.submitting = true
    this.createTarget.disabled = true
    this.formTarget.setAttribute('aria-busy', 'true')
    this.loadingTarget.textContent = 'Checking table setup…'
  }

  submissionFinished(event) {
    if (event.detail.success === false) this.submissionFailed()
  }

  submissionFailed() {
    this.submitting = false
    this.formTarget.removeAttribute('aria-busy')
    this.invalidate({keepColumnTypes: true})
    this.errorTarget.textContent = 'The request could not complete. Preview the worksheet and try again.'
  }

  creationResponse(event) {
    const stream = event.target
    if (stream.getAttribute('target') !== 'google_sheets_results') return
    if (!stream.querySelector('template')?.content.querySelector('[data-google-sheets-creation-error]')) return
    this.submitting = false
    this.formTarget.removeAttribute('aria-busy')
    this.invalidate({keepColumnTypes: true})
  }

  async request(url, body = new FormData()) {
    const response = await fetch(url, {
      method: 'POST', credentials: 'same-origin', body, signal: this.readAbort?.signal,
      headers: {'Accept': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''}
    })
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Unable to read Google Sheets. Please try again.')
    return result
  }

  loadPicker() {
    if (!pickerLibrary) {
      pickerLibrary = new Promise((resolve, reject) => {
        const load = () => window.gapi.load('picker', {callback: resolve, onerror: () => reject(new Error('Unable to load Google Picker.'))})
        if (window.gapi) return load()
        const script = document.createElement('script')
        script.src = 'https://apis.google.com/js/api.js'
        script.onload = load
        script.onerror = () => reject(new Error('Unable to load Google Picker.'))
        document.head.append(script)
      }).catch(error => { pickerLibrary = null; throw error })
    }
    return pickerLibrary
  }
}
